/**
 * WebManagedPlanNotice — the review-safe neutral copy shown to native users
 * wherever a paid surface would otherwise present a price / buy button / Stripe
 * portal link.
 *
 * Per `docs/mobile-app/STORE-PURCHASE-STRATEGY.md` §(c), the safe pattern:
 *  - States the feature is paid (so the app doesn't "feel broken").
 *  - Tells the user WHERE their account lives (the web), descriptively.
 *  - Promises auto-unlock once the plan is active.
 *  - Does NOT name a price, NOT show a URL, NOT render a tappable link/button
 *    that opens checkout or the Stripe portal. "on the web" / "in a browser" is
 *    descriptive, not a call-to-action — that distinction is what keeps it
 *    clear of App Store guideline 3.1.1 even on non-US storefronts.
 *
 * This component contains NO links and NO buttons by design. Do not add a
 * tappable "go to the website" affordance here without revisiting the strategy
 * doc — that would reintroduce store-review risk.
 */
import React from 'react';
import { Lock } from 'lucide-react';
import { cn } from '@/lib/utils';

interface WebManagedPlanNoticeProps {
  /**
   * `panel` (default) — a self-contained card, for route-level replacement of a
   * whole pricing/checkout page.
   * `inline` — a compact block, for replacing a single button/link inside an
   * otherwise-useful panel (e.g. the settings "Manage Subscription" row).
   */
  variant?: 'panel' | 'inline';
  /**
   * Optional lead line override. Defaults to the generic "part of a paid plan"
   * message; pass a feature-specific line where the surrounding context wants
   * it (e.g. "NextGen Bar Prep is part of a paid plan.").
   */
  title?: string;
  className?: string;
}

const DEFAULT_TITLE = 'This feature is part of a paid plan.';
const BODY =
  'Your JD Simplified plan is managed from your account on the web. Sign in ' +
  'at jdsimplified.com on any browser to view or change your plan. Once ' +
  'active, it unlocks here automatically.';

export function WebManagedPlanNotice({
  variant = 'panel',
  title = DEFAULT_TITLE,
  className,
}: WebManagedPlanNoticeProps) {
  if (variant === 'inline') {
    return (
      <div
        className={cn(
          'rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm dark:border-gray-700 dark:bg-gray-800/50',
          className
        )}
        role="note"
      >
        <p className="font-medium text-gray-900 dark:text-white">{title}</p>
        <p className="mt-1 text-gray-600 dark:text-gray-400">{BODY}</p>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'mx-auto flex max-w-md flex-col items-center rounded-2xl border border-gray-200 bg-white p-8 text-center shadow-sm dark:border-gray-700 dark:bg-gray-900',
        className
      )}
      role="note"
    >
      <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-orange-50 dark:bg-orange-500/10">
        <Lock className="h-6 w-6 text-[#F37022]" aria-hidden="true" />
      </div>
      <h2 className="text-xl font-bold text-gray-900 dark:text-white">{title}</h2>
      <p className="mt-3 text-sm leading-relaxed text-gray-600 dark:text-gray-400">
        {BODY}
      </p>
    </div>
  );
}
